import React from "react";
import {
    Box,
    Button,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Info as InfoIcon,
    Update as UpdateIcon,
    DataObject as MappingIcon,
    Description as DocumentationIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import { GenericModal } from "components/GenericModal";
import { GenericTabs } from "components/GenericTabs";
import {
    DestinationModalProps,
    TabType,
    TabProps,
} from "./DestinationModal.types";
import { useDestinationModal } from "./hooks/useDestinationModal";
import DestinationIcon from "./components/DestinationIcon";
import DeleteDestinationDialog from "./components/DeleteDestinationDialog";
import OverviewTab from "./components/tabs/OverviewTab";
import ReconnectTab from "./components/tabs/ReconnectTab";
import EditMappingTab from "./components/tabs/EditMappingTab";
import SwalAlertComp from "components/SwalAlert";
import { getDestinationDocumentLink } from "helpers/documentation/getDocumentationLinks";
import { CustomAudienceOverview } from "pages/Trackers/SingleTracker.helpers";

const openDocumentNewTab = (e: React.MouseEvent, url?: string) => {
    e.preventDefault();
    if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
    }
};

const TAB_ICONS: Record<TabType, React.ReactNode> = {
    overview: <InfoIcon />,
    reconnect: <UpdateIcon />,
    editMapping: <MappingIcon />,
};

const DestinationModal: React.FC<DestinationModalProps> = ({
    isOpen,
    destination,
    tracker,
    language,
    onClose,
    onRefresh,
    initialTab = null,
    mode = "tracker",
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const {
        isDeleteDialogOpen,
        availableTabs,
        activeTab,
        setActiveTab,
        openDeleteDialog,
        closeDeleteDialog,
        refreshTracker,
        handleClose,
        getTabLabel,
        integrationLogs,
        isLoadingLogs,
    } = useDestinationModal({
        destination,
        tracker,
        onClose,
        onRefresh,
        initialTab,
        mode,
    });

    const destinationCode = destination?.destination_type?.code;

    const tabProps: TabProps = {
        destination,
        tracker,
        language,
        onRefresh: refreshTracker,
        onClose: handleClose,
        mode,
    };

    const renderTabContent = (tabType: TabType): React.ReactNode => {
        switch (tabType) {
            case "overview":
                if (destinationCode === "fbca") {
                    return (
                        <div>
                            <CustomAudienceOverview destination={destination} />

                            <OverviewTab {...tabProps} />
                        </div>
                    );
                }
                return <OverviewTab {...tabProps} />;
            case "reconnect":
                return (
                    <ReconnectTab
                        {...tabProps}
                        integrationLogs={integrationLogs}
                        isLoadingLogs={isLoadingLogs}
                    />
                );
            case "editMapping":
                return (
                    <EditMappingTab
                        {...tabProps}
                        destinationId={destination?.id}
                    />
                );
            default:
                return null;
        }
    };

    const isDisconnected = destination?.status === 0;
    const isDeleteDisabled =
        mode === "standalone" && (destination?.trackers?.length ?? 0) > 0;

    const tabs = availableTabs.map((tabType) => ({
        label: getTabLabel(tabType, language),
        value: tabType,
        content: renderTabContent(tabType),
        icon: TAB_ICONS[tabType],
        disabled: isDisconnected && tabType !== "reconnect",
    }));

    const docUrl = getDestinationDocumentLink(destinationCode);

    const documentationLabel =
        language?.buttons?.documentation ?? "Documentation";
    const deleteLabel =
        mode === "standalone"
            ? "Delete Destination"
            : (language?.buttons?.remove_destination ?? "Remove Destination");

    const handleDocumentationClick = (e: React.MouseEvent) => {
        if (docUrl && docUrl !== "") {
            openDocumentNewTab(e, docUrl);
        } else {
            SwalAlertComp({
                title: "Documentation Not Available",
                message: `Documentation for ${
                    destination?.destination_type?.name ||
                    "this destination type"
                } is not available yet. Please contact support for assistance.`,
                icon: "info",
                timer: 4000,
            });
        }
    };

    const headerActions = (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.5, sm: 1 },
            }}
        >
            {docUrl &&
                (isMobile ? (
                    <Tooltip title={documentationLabel} placement="bottom">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={handleDocumentationClick}
                        >
                            <DocumentationIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DocumentationIcon />}
                        onClick={handleDocumentationClick}
                        color="primary"
                        sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}
                    >
                        {documentationLabel}
                    </Button>
                ))}
            <Tooltip
                title={
                    isDeleteDisabled
                        ? (language?.destinations?.cannot_delete_in_use ??
                          "This destination is in use by a tracker and cannot be deleted.")
                        : ""
                }
                placement="bottom"
            >
                <span>
                    {isMobile ? (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={openDeleteDialog}
                            disabled={isDeleteDisabled}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    ) : (
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={openDeleteDialog}
                            disabled={isDeleteDisabled}
                            sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}
                        >
                            {deleteLabel}
                        </Button>
                    )}
                </span>
            </Tooltip>
        </Box>
    );

    return (
        <>
            <GenericModal
                open={isOpen}
                onClose={handleClose}
                title={destination?.destination_type?.name}
                subtitle={destination?.partner_reference_id}
                headerColor="#f5f5f5"
                icon={
                    <DestinationIcon
                        code={destinationCode}
                        size="medium"
                        alt={destination?.destination_type?.name}
                    />
                }
                contentPadding={0}
                content={
                    <GenericTabs
                        tabs={tabs}
                        defaultTab={activeTab}
                        animation="fade"
                        showBadges={false}
                        elevation={0}
                        contentPadding={isMobile ? 2 : 3}
                        variant="scrollable"
                        onTabChange={(value) => setActiveTab(value as TabType)}
                        actionButtons={headerActions}
                    />
                }
                maxWidth="lg"
                fixedHeight="90vh"
            />

            <DeleteDestinationDialog
                open={isDeleteDialogOpen}
                onClose={closeDeleteDialog}
                destination={destination}
                tracker={tracker}
                language={language}
                onDeleteSuccess={handleClose}
                onRefresh={refreshTracker}
                mode={mode}
            />
        </>
    );
};

export default DestinationModal;
